import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import { ActiveQuestionnairesWithoutElementDocument } from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { PowerCollage } from "@/components/grow/PowerCollage";

// Power Quiz group overview: the quiz is split into parts ("Power Quiz ·
// Teil 1".."Teil 6", 15 items each) seeded as element-free questionnaires.
// activeQuestionnairesWithoutElement returns them name-ordered with a per-part
// answered flag, which is what makes part-level resume work without any
// backend session state. The header artwork is the PowerCollage: each power
// tile uncovers only when its self-assessment is complete - answering the quiz
// never changes the blur.

export function PowerQuizPage() {
  const intl = useIntl();
  const navigate = useNavigate();

  // cache-and-network so the answered flags refresh when returning from a part.
  const { data, loading } = useQuery(ActiveQuestionnairesWithoutElementDocument, {
    fetchPolicy: "cache-and-network",
  });
  const parts = data?.activeQuestionnairesWithoutElement ?? [];
  const total = parts.length;
  const answeredCount = parts.reduce((n, p) => (p.answered ? n + 1 : n), 0);
  const next = parts.find((p) => !p.answered);

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="ActiveQuestionnairesWithoutElement"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-xs tracking-wide text-ink/55">
            <FormattedMessage id="grow.play.quiz.eyebrow" defaultMessage="Grow · Play" />
          </p>
          <h1 className="mt-1 text-3xl font-semibold">
            <FormattedMessage id="grow.play.quiz.title" defaultMessage="Power Quiz" />
          </h1>
          {parts[0]?.questionnaire.instructions ? (
            <p className="mt-2 text-sm text-ink/55">{parts[0].questionnaire.instructions}</p>
          ) : null}
        </div>
        <PowerCollage className="hidden aspect-[6/5] w-56 shrink-0 rounded-xl ring-1 ring-black/10 sm:block" />
      </header>

      {loading && total === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : total === 0 ? (
        <NoBackend
          label={intl.formatMessage({
            id: "grow.play.quiz.missing",
            defaultMessage: "The Power Quiz is not set up on this environment yet.",
          })}
        />
      ) : (
        <div className="space-y-6 pb-24">
          <div className="text-[11px] text-ink/55">
            <FormattedMessage
              id="grow.play.quiz.partsProgress"
              defaultMessage="{answered} of {total} parts answered"
              values={{ answered: answeredCount, total }}
            />
          </div>

          <div className="flex flex-col gap-2">
            {parts.map((p, i) => {
              const isNext = p.questionnaire.questionnaireId === next?.questionnaire.questionnaireId;
              return (
                <button
                  key={p.questionnaire.questionnaireId}
                  type="button"
                  onClick={() => navigate(`/grow/play/power-quiz/${p.questionnaire.questionnaireId}`)}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-left text-[14px] ring-1 transition focus:outline-none ${
                    p.answered
                      ? "bg-ink text-on-ink ring-ink"
                      : `bg-white text-ink/70 hover:text-ink hover:ring-accent ${
                          isNext ? "ring-2 ring-accent/60" : "ring-black/15"
                        }`
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-medium ${
                      p.answered ? "bg-cream/20 text-on-ink" : "bg-ink/[0.06] text-ink/60"
                    }`}
                  >
                    {p.answered ? "✓" : i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <FormattedMessage
                      id="grow.play.quiz.partLabel"
                      defaultMessage="Part {index}"
                      values={{ index: i + 1 }}
                    />
                  </span>
                  <span className={`text-[11px] ${p.answered ? "text-on-ink/70" : "text-ink/45"}`}>
                    {p.answered ? (
                      <FormattedMessage id="grow.play.quiz.partAnswered" defaultMessage="answered" />
                    ) : (
                      <FormattedMessage id="grow.play.quiz.partOpen" defaultMessage="open" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex min-h-9 items-center justify-end">
            {next ? (
              <button
                type="button"
                onClick={() => navigate(`/grow/play/power-quiz/${next.questionnaire.questionnaireId}`)}
                className="smycles-button px-5 py-2.5 text-[11px]"
              >
                {answeredCount === 0 ? (
                  <FormattedMessage id="grow.play.quiz.start" defaultMessage="Start quiz" />
                ) : (
                  <FormattedMessage
                    id="grow.play.quiz.continue"
                    defaultMessage="Continue with part {index}"
                    values={{ index: parts.indexOf(next) + 1 }}
                  />
                )}
              </button>
            ) : (
              <p className="text-sm text-ink/55">
                <FormattedMessage
                  id="grow.play.quiz.allAnswered"
                  defaultMessage="All parts answered."
                />
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
